import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Wand2, Sparkles, ExternalLink, ArrowLeft, CheckCircle2, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import {
  useProposeTrendArticles,
  useGenerateTrendArticle,
  type TrendProposal,
} from '@/hooks/useWebTrends';
import { useNewsCategories } from '@/hooks/useNewsCategories';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = 'range' | 'proposals' | 'generating';

interface GenResult {
  title: string;
  article_id?: string;
  error?: string;
}

function todayISO(offsetDays = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

export default function AnalyzeTrendsDialog({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('range');
  const [dateFrom, setDateFrom] = useState(todayISO(-7));
  const [dateTo, setDateTo] = useState(todayISO(0));
  const [statusFilter, setStatusFilter] = useState<'nueva' | 'all'>('nueva');
  const [includeCoverImage, setIncludeCoverImage] = useState(true);
  const [proposals, setProposals] = useState<TrendProposal[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [categoryOverrides, setCategoryOverrides] = useState<Record<number, string>>({});
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState<GenResult[]>([]);

  const proposeMut = useProposeTrendArticles();
  const generateMut = useGenerateTrendArticle();
  const { data: categories = [] } = useNewsCategories();

  const reset = () => {
    setStep('range');
    setProposals([]);
    setSelected(new Set());
    setExpanded(new Set());
    setCategoryOverrides({});
    setProgress({ done: 0, total: 0 });
    setResults([]);
  };

  const handleClose = (o: boolean) => {
    if (!o && step === 'generating' && progress.done < progress.total) {
      // don't allow closing mid-batch
      return;
    }
    onOpenChange(o);
    if (!o) setTimeout(reset, 300);
  };

  const handleAnalyze = async () => {
    try {
      const res = await proposeMut.mutateAsync({
        date_from: dateFrom,
        date_to: dateTo + 'T23:59:59.999Z',
        status: statusFilter,
      });
      if (!res.proposals?.length) {
        toast.error('La IA no encontró historias relevantes en ese rango');
        return;
      }
      setProposals(res.proposals);
      setSelected(new Set(res.proposals.map((_, i) => i)));
      setStep('proposals');
      toast.success(`${res.proposals.length} propuestas listas (de ${res.trend_count} tendencias)`);
    } catch (e: any) {
      toast.error(e?.message ?? 'Error al analizar tendencias');
    }
  };

  const toggleSel = (i: number) => {
    setSelected((p) => {
      const n = new Set(p);
      if (n.has(i)) n.delete(i); else n.add(i);
      return n;
    });
  };

  const toggleExp = (i: number) => {
    setExpanded((p) => {
      const n = new Set(p);
      if (n.has(i)) n.delete(i); else n.add(i);
      return n;
    });
  };

  const selectedProposals = useMemo(
    () => Array.from(selected).sort((a, b) => a - b).map((i) => ({ idx: i, p: proposals[i] })),
    [selected, proposals]
  );

  const handleGenerate = async () => {
    if (selectedProposals.length === 0) return;
    setStep('generating');
    setProgress({ done: 0, total: selectedProposals.length });
    setResults([]);

    for (const { idx, p } of selectedProposals) {
      try {
        const catId = categoryOverrides[idx] ?? p.suggested_category_id ?? null;
        const res = await generateMut.mutateAsync({
          trend_ids: p.trend_ids,
          category_id: catId || null,
          suggested_title: p.title,
          hook: p.hook,
          include_cover_image: includeCoverImage,
        });
        setResults((prev) => [...prev, { title: p.title, article_id: res.article_id }]);
      } catch (e: any) {
        setResults((prev) => [...prev, { title: p.title, error: e?.message ?? 'Error' }]);
      } finally {
        setProgress((pr) => ({ ...pr, done: pr.done + 1 }));
        // small pause to be gentle with rate limits
        await new Promise((r) => setTimeout(r, 800));
      }
    }
    toast.success('Generación en batch completada');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-primary" />
            Analizar tendencias con IA
          </DialogTitle>
          <DialogDescription>
            La IA agrupa tendencias relacionadas y propone artículos con títulos de alto CTR.
          </DialogDescription>
        </DialogHeader>

        {step === 'range' && (
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-2 pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Desde</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div>
                <Label>Hasta</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
            </div>
            <div>
              <Label>Origen</Label>
              <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nueva">Solo tendencias nuevas (no usadas)</SelectItem>
                  <SelectItem value="all">Todas las tendencias del rango</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5 pr-3">
                <Label htmlFor="cover-image-switch">Generar imagen de portada con IA</Label>
                <p className="text-xs text-muted-foreground">
                  Crea una imagen editorial para cada borrador generado.
                </p>
              </div>
              <Switch id="cover-image-switch" checked={includeCoverImage} onCheckedChange={setIncludeCoverImage} />
            </div>
            <div className="rounded-lg bg-muted/40 p-3 text-sm text-muted-foreground">
              La IA leerá las tendencias importadas en ese rango (máx 100) y propondrá entre 5 y 10 historias
              agrupando noticias relacionadas. Cada historia tendrá un título atractivo y específico optimizado
              para compartir en WhatsApp / redes.
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => handleClose(false)}>Cancelar</Button>
              <Button onClick={handleAnalyze} disabled={proposeMut.isPending || !dateFrom || !dateTo}>
                {proposeMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                Analizar
              </Button>
            </div>
          </div>
        )}


        {step === 'proposals' && (
          <div className="flex-1 flex flex-col min-h-0 gap-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {proposals.length} historias propuestas · {selected.size} seleccionadas
              </span>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={() => setSelected(new Set(proposals.map((_, i) => i)))}>Todas</Button>
                <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Ninguna</Button>
              </div>
            </div>
            <ScrollArea className="flex-1 min-h-0 -mx-6 px-6">
              <div className="space-y-3">
                {proposals.map((p, i) => {
                  const isSel = selected.has(i);
                  const isExp = expanded.has(i);
                  const catId = categoryOverrides[i] ?? p.suggested_category_id ?? '';
                  return (
                    <div key={i} className={`rounded-lg border p-3 transition-colors ${isSel ? 'border-primary/40 bg-primary/5' : 'border-border'}`}>
                      <div className="flex gap-3">
                        <Checkbox checked={isSel} onCheckedChange={() => toggleSel(i)} className="mt-1" />
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="flex items-start justify-between gap-2">
                            <h4 className="font-semibold leading-snug">{p.title}</h4>
                            <Badge variant="outline" className="shrink-0">★ {p.attractiveness_score}/10</Badge>
                          </div>
                          <p className="text-sm text-muted-foreground italic">{p.hook}</p>
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <Select value={catId} onValueChange={(v) => setCategoryOverrides((prev) => ({ ...prev, [i]: v }))}>
                              <SelectTrigger className="h-8 w-[220px] text-xs"><SelectValue placeholder="Categoría (opcional)" /></SelectTrigger>
                              <SelectContent>
                                {categories.map((c) => (
                                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => toggleExp(i)}>
                              {isExp ? <ChevronDown className="h-3 w-3 mr-1" /> : <ChevronRight className="h-3 w-3 mr-1" />}
                              {p.trends.length} fuente{p.trends.length === 1 ? '' : 's'}
                            </Button>
                          </div>
                          {isExp && (
                            <ul className="text-xs space-y-1 pl-4 border-l border-border">
                              {p.trends.map((t) => (
                                <li key={t.id} className="flex items-start gap-2">
                                  <span className="text-muted-foreground shrink-0">[{t.source_name ?? 'Web'}]</span>
                                  <a href={t.url} target="_blank" rel="noreferrer" className="hover:underline flex items-center gap-1">
                                    {t.title}
                                    <ExternalLink className="h-3 w-3 inline" />
                                  </a>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
            <div className="shrink-0 flex justify-between gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setStep('range')}>
                <ArrowLeft className="mr-2 h-4 w-4" />Volver
              </Button>
              <Button onClick={handleGenerate} disabled={selected.size === 0}>
                <Sparkles className="mr-2 h-4 w-4" />
                Generar {selected.size} borrador{selected.size === 1 ? '' : 'es'}
              </Button>
            </div>
          </div>
        )}

        {step === 'generating' && (
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-2 pr-1">

            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="font-medium">Generando borradores…</span>
                <span className="text-muted-foreground">{progress.done} / {progress.total}</span>
              </div>
              <Progress value={progress.total > 0 ? (progress.done / progress.total) * 100 : 0} />
            </div>
            <ScrollArea className="max-h-[50vh]">
              <div className="space-y-2">
                {results.map((r, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded border text-sm">
                    {r.error ? (
                      <AlertCircle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium line-clamp-1">{r.title}</div>
                      {r.error && <div className="text-xs text-destructive mt-1">{r.error}</div>}
                    </div>
                    {r.article_id && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          handleClose(false);
                          navigate(`/trends-management?edit=${r.article_id}`);
                        }}
                      >
                        Abrir
                      </Button>
                    )}
                  </div>
                ))}
                {progress.done < progress.total && (
                  <div className="flex items-center gap-2 p-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Trabajando en la siguiente…
                  </div>
                )}
              </div>
            </ScrollArea>
            {progress.done >= progress.total && progress.total > 0 && (
              <div className="flex justify-end">
                <Button onClick={() => handleClose(false)}>Cerrar</Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
